import type { FC } from "react"
import { useRef } from "react"
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Stack,
  Text,
  Flex,
  useDisclosure,
  Icon,
  Box,
} from "@chakra-ui/react"

import { useAddress } from "web3/hooks/useAddress"
import { EXTERNAL_ROUTES } from "common/constants/routes"
import type { Governor, Proposal } from "query/graphql"
import { ProposalStatus, GovernorType } from "query/graphql"
import Web3Button from "web3/components/Web3Button"
import { useWithProposalErrorCatch } from "governance/hooks/useWithProposalErrorCatch"
import ProposalLearnMoreAboutButton from "proposal/components/ctas/ProposalLearnMoreAboutButton"
import { useCancelProposal } from "proposal/hooks/useCancelProposal"
import { Ban } from "ui/components/icons/font-awesome/Ban"
import { useModals } from "common/hooks/useModals"
import InformationIcon from "ui/components/icons/InformationIcon"
import { useMe } from "user/providers/MeProvider"

type ProposalCancelButtonProps = {
  governance: Governor
  proposal: Proposal
}
const ProposalCancelButton: FC<ProposalCancelButtonProps> = ({
  governance,
  proposal,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const address = useAddress()
  const { walletSelection } = useModals()
  const { withErrorCatch } = useWithProposalErrorCatch()

  const { cancelProposal, cancelFromTimelock } = useCancelProposal({
    proposal,
    governance,
  })

  const { timelockId, contracts } = governance
  const { type: governorType } = contracts.governor

  const isProposalQueued = proposal.status === ProposalStatus.Queued
  const isOZWithTimelock =
    governorType === GovernorType.Openzeppelingovernor && Boolean(timelockId)

  const handleCancel = async (): Promise<void> => {
    if (address) {
      if (isProposalQueued && isOZWithTimelock) {
        await withErrorCatch(cancelFromTimelock)
      } else {
        await withErrorCatch(cancelProposal)
      }
    } else {
      walletSelection.onOpen()
    }
  }

  const handleOnConfirm = () => {
    onClose()
    handleCancel()
  }

  const handleOnCreateManualTx = () => {
    if (address) {
      if (isProposalQueued && isOZWithTimelock) {
        cancelFromTimelock({ viewTxDetails: true })
      } else {
        cancelProposal({ viewTxDetails: true })
      }
    }
  }

  return (
    <>
      <Web3Button
        leftIcon={<Icon as={Ban} />}
        requiredChainId={governance.chainId}
        size="sm"
        variant="tertiary"
        width="100%"
        onValid={onOpen}
      >
        <Flex width="100%">Cancel proposal</Flex>
      </Web3Button>
      <CancelProposalConfirmDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleOnConfirm}
        onCreateManualTx={handleOnCreateManualTx}
      />
    </>
  )
}

type CancelProposalConfirmDialogProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  onCreateManualTx: () => void
}
const CancelProposalConfirmDialog: FC<CancelProposalConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onCreateManualTx,
}) => {
  const me = useMe()
  const canUseSafeTx = me?.type === "SAFE"
  const cancelRef = useRef(null)

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg">Cancel proposal</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <Stack spacing={3}>
              <Text textStyle="body.regular.md">
                Once cancelled, a proposal can no longer be executed. Please
                note: Proposals can only be cancelled in certain scenarios.
              </Text>
              <ProposalLearnMoreAboutButton
                color="gray.800"
                label="Learn more"
                url={EXTERNAL_ROUTES.tally.docs.cancelingProposals()}
                variant="link"
                width="auto"
              />

              {canUseSafeTx ? (
                <ProposalSafeCancelDetails onClick={onCreateManualTx} />
              ) : null}
            </Stack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>
              Close
            </Button>
            <Button colorScheme="red" ml={3} onClick={onConfirm}>
              Cancel proposal
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}

type ProposalSafeCancelDetailsProps = {
  onClick: () => void
}
const ProposalSafeCancelDetails: FC<ProposalSafeCancelDetailsProps> = ({
  onClick,
}) => {
  return (
    <Box>
      <Button
        color="gray.800"
        leftIcon={<Icon as={InformationIcon} color="gray.800" />}
        px={0}
        size="sm"
        variant="link"
        onClick={onClick}
      >
        View transaction details
      </Button>
    </Box>
  )
}

export default ProposalCancelButton
